import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AnalyticsFilters = ({ onFiltersChange, isLoading }) => {
  const [filters, setFilters] = useState({
    dateRange: 'last30days',
    startDate: '',
    endDate: '',
    doorFilter: 'all',
    userGroup: 'all',
    timeRange: 'all'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const doorOptions = [
    { value: 'all', label: 'All Doors' },
    { value: 'main-entrance', label: 'Main Entrance' },
    { value: 'server-room', label: 'Server Room' },
    { value: 'office-a', label: 'Office A' },
    { value: 'office-b', label: 'Office B' },
    { value: 'conference-room', label: 'Conference Room' }
  ];

  const userGroupOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'employees', label: 'Employees' },
    { value: 'contractors', label: 'Contractors' },
    { value: 'visitors', label: 'Visitors' },
    { value: 'admins', label: 'Administrators' }
  ];

  const timeRangeOptions = [
    { value: 'all', label: 'All Hours' },
    { value: 'business', label: 'Business Hours (9AM-6PM)' },
    { value: 'after-hours', label: 'After Hours (6PM-9AM)' },
    { value: 'weekend', label: 'Weekends Only' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      dateRange: 'last30days',
      startDate: '',
      endDate: '',
      doorFilter: 'all',
      userGroup: 'all',
      timeRange: 'all'
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const handleExport = () => {
    // Mock export functionality
    const exportData = {
      filters,
      timestamp: new Date()?.toISOString(),
      format: 'CSV'
    };
    
    console.log('Exporting analytics data:', exportData);
    
    // Simulate file download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 security-shadow-card mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Analytics Filters</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            iconName="Download"
            iconPosition="left"
            disabled={isLoading}
          >
            Export CSV
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>
      </div>
      {/* Basic Filters - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Date Range</label>
          <select
            value={filters?.dateRange}
            onChange={(e) => handleFilterChange('dateRange', e?.target?.value)}
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
          >
            {dateRangeOptions?.map(option => (
              <option key={option?.value} value={option?.value}>{option?.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Door Filter</label>
          <select
            value={filters?.doorFilter}
            onChange={(e) => handleFilterChange('doorFilter', e?.target?.value)}
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
          >
            {doorOptions?.map(option => (
              <option key={option?.value} value={option?.value}>{option?.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">User Group</label>
          <select
            value={filters?.userGroup}
            onChange={(e) => handleFilterChange('userGroup', e?.target?.value)}
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
          >
            {userGroupOptions?.map(option => (
              <option key={option?.value} value={option?.value}>{option?.label}</option>
            ))}
          </select>
        </div>
      </div>
      {/* Custom Date Range - Show when custom is selected */}
      {filters?.dateRange === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input
            label="Start Date"
            type="date"
            value={filters?.startDate}
            onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
          />
          <Input
            label="End Date"
            type="date"
            value={filters?.endDate}
            onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
          />
        </div>
      )}
      {/* Advanced Filters - Show when expanded */}
      {isExpanded && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Time Range</label>
              <select
                value={filters?.timeRange}
                onChange={(e) => handleFilterChange('timeRange', e?.target?.value)}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                {timeRangeOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={handleReset}
                iconName="RotateCcw"
                iconPosition="left"
                className="w-full"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Active Filters Summary */}
      <div className="flex flex-wrap gap-2 mt-4">
        {Object.entries(filters)?.map(([key, value]) => {
          if (value && value !== 'all' && value !== 'last30days') {
            return (
              <span
                key={key}
                className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
              >
                {key}: {value}
                <button
                  onClick={() => handleFilterChange(key, key === 'dateRange' ? 'last30days' : 'all')}
                  className="ml-1 hover:text-primary/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default AnalyticsFilters;